import { useState, useEffect, useContext, useMemo } from 'react';
import { MultidimensionalAllocationContext } from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/MultidimensionalAllocationReducer';

/**
 * Custom hook for managing allocation data fetching and state
 * Encapsulates the logic for loading, transforming, and managing allocation calculations
 */
export const useAllocationData = () => {
    const { services, multidimensionalAllocationState, multidimensionalAllocationDispatch } = useContext(MultidimensionalAllocationContext);
    const [shouldLoad, setShouldLoad] = useState(false);

    const { isLoading, error, allocationCalculations } = multidimensionalAllocationState;

    // Use the service hook to get data
    const [serviceResult, serviceLoading, serviceError] = services.multidimensionalAllocationService.getMultiAllocationWorksheets(
        shouldLoad ? 'load' : '',
        undefined
    );

    // Trigger data loading on hook initialization
    useEffect(() => {
        setShouldLoad(true);
    }, []);

    // Update state when service returns data
    useEffect(() => {
        multidimensionalAllocationDispatch(ACTIONS.SET_LOADING.withPayload(serviceLoading));
    }, [serviceLoading, multidimensionalAllocationDispatch]);

    useEffect(() => {
        if (serviceError) {
            multidimensionalAllocationDispatch(ACTIONS.SET_ERROR.withPayload(serviceError));
        }
    }, [serviceError, multidimensionalAllocationDispatch]);

    useEffect(() => {
        if (serviceResult) {
            multidimensionalAllocationDispatch(ACTIONS.SET_ALLOCATION_CALCULATIONS.withPayload(serviceResult));
        }
    }, [serviceResult, multidimensionalAllocationDispatch]);

    // Transform data for display with memoization
    const worksheets = useMemo(() => {
        return allocationCalculations ? allocationCalculations.worksheets : [];
    }, [allocationCalculations]);

    // Refresh data function
    const refreshData = () => {
        setShouldLoad(false);
        setTimeout(() => setShouldLoad(true), 0);
    };

    return {
        isLoading,
        error,
        worksheets,
        itemCount: worksheets.length,
        refreshData,
        services
    };
};
